import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetEventsSummaryXlsxMutation } from '../store';
import FilterTable, { BaseData } from './Table/Table';
import Button from './atoms/Button';
import { EventSummaryDTO } from '../models/event';
import { BusinessDTO } from '../models/business';
import { formatCurrency } from '../utils/shared';
import { formatDateForTable } from '../utils/dates';
import getToastColor from '../utils/getToastColor';
import getToastText from '../utils/getToastText';
import { PAYMENT_METHOD_LABELS, PaymentMethod } from '../models/payment';

interface TableEventSummary extends BaseData {
  id: string;
  event_name: JSX.Element;
  business_name: JSX.Element;
  status: JSX.Element;
  start_date: JSX.Element;
  end_date: JSX.Element;
  total_money_processed: JSX.Element;
  money_in_our_hands: JSX.Element;
  money_in_their_hands: JSX.Element;
  mp_fees: JSX.Element;
  liquidation_amount: JSX.Element;
  total_tickets_sold: string;
  total_service_charge: JSX.Element;
  online_service_charge_percentage: JSX.Element;
  manual_service_charge_percentage: JSX.Element;
  online_service_charge_total: JSX.Element;
  manual_service_charge_total: JSX.Element;
  payment_method: JSX.Element;
  last_sell_date: JSX.Element;
}

interface EventsSummaryTableProps {
  eventsSummary: EventSummaryDTO[];
  businesses: BusinessDTO[];
}

const EventsSummaryTable: React.FC<EventsSummaryTableProps> = ({ eventsSummary, businesses }) => {
  const navigate = useNavigate();
  const [getEventsSummaryXlsx, { isLoading: isLoadingXlsx }] = useGetEventsSummaryXlsxMutation();

  // Create a mapping from business name to business ID
  const businessNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    businesses.forEach((business) => {
      map.set(business.name, business.id);
    });
    return map;
  }, [businesses]);

  const config = [
    {
      label: 'Evento',
      id: 'event_name',
    },
    {
      label: 'Productora',
      id: 'business_name',
    },
    {
      label: 'Estado',
      id: 'status',
    },
    {
      label: 'Fecha inicio',
      id: 'start_date',
    },
    {
      label: 'Fecha fin',
      id: 'end_date',
    },
    {
      label: 'Dinero procesado',
      id: 'total_money_processed',
    },
    {
      label: 'Dinero en Avantti',
      id: 'money_in_our_hands',
    },
    {
      label: 'Dinero en Productor',
      id: 'money_in_their_hands',
    },
    {
      label: 'Comisión Mercado Pago',
      id: 'mp_fees',
    },
    {
      label: 'Monto de liquidación',
      id: 'liquidation_amount',
    },
    {
      label: 'Entradas vendidas',
      id: 'total_tickets_sold',
    },
    {
      label: 'Comisión total',
      id: 'total_service_charge',
    },
    {
      label: '% Comisión Online',
      id: 'online_service_charge_percentage',
    },
    {
      label: '% Comisión Manual',
      id: 'manual_service_charge_percentage',
    },
    {
      label: 'Comisión Online Total',
      id: 'online_service_charge_total',
    },
    {
      label: 'Comisión Manual Total',
      id: 'manual_service_charge_total',
    },
    {
      label: 'Métodos de pago',
      id: 'payment_method',
    },
    {
      label: 'Última venta',
      id: 'last_sell_date',
    },
  ];

  const sortedEventsSummary = useMemo(() => [...eventsSummary].sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()), [eventsSummary]);

  const rows: TableEventSummary[] = useMemo(() => sortedEventsSummary.map((event) => {
    const startDateStr = event.start_date instanceof Date ? event.start_date.toISOString() : String(event.start_date);
    const endDateStr = event.end_date instanceof Date ? event.end_date.toISOString() : String(event.end_date);
    let lastSellDateStr: string | null = null;
    if (event.last_sell_date instanceof Date) {
      lastSellDateStr = event.last_sell_date.toISOString();
    } else if (event.last_sell_date) {
      lastSellDateStr = String(event.last_sell_date);
    }

    const businessId = businessNameToIdMap.get(event.business_name);

    const handleEventNameClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (businessId) {
        navigate(`/admin/${businessId}/events/${event.event_id}`);
      }
    };

    const handleBusinessNameClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (businessId) {
        navigate(`/admin/${businessId}/events`);
      }
    };

    return {
      id: event.event_id,
      event_name: (
        <div
          className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[150px] cursor-pointer hover:text-primary-600 transition-colors underline"
          onClick={handleEventNameClick}
        >
          {event.event_name}
        </div>
      ),
      business_name: (
        <div
          className="whitespace-nowrap text-ellipsis overflow-hidden max-w-[150px] cursor-pointer hover:text-primary-600 transition-colors underline"
          onClick={handleBusinessNameClick}
        >
          {event.business_name}
        </div>
      ),
      status: <p className={`${getToastColor(event.status)} px-[10px] py-[2px] rounded-3xl w-fit text-sm-medium text-center items-center whitespace-nowrap`}>{getToastText(event.status)}</p>,
      start_date: (
        <div className="whitespace-nowrap">
          {formatDateForTable(startDateStr)}
        </div>
      ),
      end_date: (
        <div className="whitespace-nowrap">
          {formatDateForTable(endDateStr)}
        </div>
      ),
      total_money_processed: <div className="whitespace-nowrap">{formatCurrency(event.total_money_processed)}</div>,
      money_in_our_hands: <div className="whitespace-nowrap">{formatCurrency(event.money_in_our_hands)}</div>,
      money_in_their_hands: <div className="whitespace-nowrap">{formatCurrency(event.money_in_their_hands)}</div>,
      mp_fees: <div className="whitespace-nowrap">{formatCurrency(event.mp_fees)}</div>,
      liquidation_amount: <div className="whitespace-nowrap">{formatCurrency(event.liquidation_amount)}</div>,
      total_tickets_sold: event.total_tickets_sold.toString(),
      total_service_charge: <div className="whitespace-nowrap">{formatCurrency(event.total_service_charge)}</div>,
      online_service_charge_percentage: <div className="whitespace-nowrap">{event.online_service_charge_percentage * 100}%</div>,
      manual_service_charge_percentage: <div className="whitespace-nowrap">{event.manual_service_charge_percentage * 100}%</div>,
      online_service_charge_total: <div className="whitespace-nowrap">{formatCurrency(event.online_service_charge_total)}</div>,
      manual_service_charge_total: <div className="whitespace-nowrap">{formatCurrency(event.manual_service_charge_total)}</div>,
      payment_method: (
        <div className="whitespace-nowrap">
          {Object.entries(event.payment_method)
            .filter(([, amount]) => amount > 0)
            .map(([method]) => PAYMENT_METHOD_LABELS[method as PaymentMethod])
            .join(', ')}
        </div>
      ),
      last_sell_date: lastSellDateStr ? (
        <div className="whitespace-nowrap">{formatDateForTable(lastSellDateStr)}</div>
      ) : (
        <div className="text-text-tertiary">-</div>
      ),
    };
  }), [sortedEventsSummary, businessNameToIdMap, navigate]);

  return (
    <FilterTable
      rows={rows}
      headCells={config}
      pagination={{ enabled: true, pageSize: 10 }}
      searchEnabled={true}
      tableId="events-summary-table"
      filterColumns
      customButtons={[<Button onClick={() => getEventsSummaryXlsx()} loading={isLoadingXlsx} variant="secondary" className="whitespace-nowrap">Descargar</Button>]}
    />
  );
};

export default EventsSummaryTable;
